import { useEffect, useState } from "react";
import { RotateCcw, TrendingDown, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, euro } from "@/lib/api";
import { DailyChart } from "./DailyChart";
import { HourlyChart } from "./HourlyChart";

const LABELS = { advisor_viewed: "Visite", test_started: "Test avviati", test_completed: "Test completati", routine_generated: "Routine create", go_to_cart: "Click carrello" };
const OTHER = ["question_answered", "profile_generated", "product_viewed", "product_selected", "product_removed", "alternative_selected"];

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div><p className="fs-eyebrow">{eyebrow}</p><h1 className="font-serif text-3xl sm:text-4xl mt-2">{title}</h1></div>
    {children}
  </div>
);

const PERIODS = [["today", "Oggi"], ["7d", "7 giorni"], ["30d", "30 giorni"], ["all", "Sempre"]];

const PERIOD_PREV = { today: "vs ieri", "7d": "vs 7 giorni prima", "30d": "vs 30 giorni prima" };

const Delta = ({ count, previous, period }) => {
  if (previous === null || previous === undefined) return null;
  const pct = previous ? Math.round(((count - previous) / previous) * 100) : (count ? null : 0);
  const up = pct === null ? count > 0 : pct > 0;
  const flat = pct === 0;
  const cls = flat ? "text-[#8C8275] bg-[#F5F3EF]" : up ? "text-[#2F6B3A] bg-[#E9F3EA]" : "text-[#9B3B2E] bg-[#F9E9E6]";
  const label = pct === null ? "Nuovo" : `${pct > 0 ? "+" : ""}${pct}%`;
  return (
    <p className="mt-2 flex items-center gap-2 text-[11px]" data-testid="funnel-delta">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full tabular-nums ${cls}`}>
        {!flat && pct !== null && (up ? <TrendingUp size={11} strokeWidth={1.8} /> : <TrendingDown size={11} strokeWidth={1.8} />)}{label}
      </span>
      <span className="text-[#8C8275]">{PERIOD_PREV[period]} ({previous})</span>
    </p>
  );
};

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(() => localStorage.getItem("fs_admin_period") || "30d");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = (p = period) => api.get("/admin/analytics", { params: { period: p } }).then((r) => setData(r.data));
  useEffect(() => { localStorage.setItem("fs_admin_period", period); load(period); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/analytics/reset");
      toast.success(`Contatori azzerati (${r.data.deleted} eventi eliminati)`);
      setConfirm(false);
      await load();
    } catch { toast.error("Impossibile azzerare i contatori"); } finally { setBusy(false); }
  };
  if (!data) return <p className="text-sm text-[#8C8275]">Caricamento…</p>;
  const top = data.funnel[0]?.count || 1;

  return (
    <div data-testid="admin-analytics-page">
      <PageTitle eyebrow="Dashboard" title="Analisi funnel">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex border border-[#E2DACF] bg-white p-1" role="tablist" data-testid="analytics-period-switch">
            {PERIODS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={period === k} data-testid={`analytics-period-${k}`} onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase transition-colors ${period === k ? "bg-[#1A1A18] text-[#FAF8F5]" : "text-[#595652] hover:text-[#1A1A18]"}`}>{label}</button>
            ))}
          </div>
          {confirm ? (
            <div className="flex items-center gap-2 text-sm" data-testid="analytics-reset-confirm">
              <span className="text-[#595652]">Azzerare tutti i contatori? I lead non vengono toccati.</span>
              <button data-testid="analytics-reset-confirm-btn" disabled={busy} onClick={reset} className="px-4 py-2 bg-[#1A1A18] text-[#FAF8F5] text-xs tracking-[0.14em] uppercase disabled:opacity-50">{busy ? "Azzero…" : "Sì, azzera"}</button>
              <button data-testid="analytics-reset-cancel-btn" onClick={() => setConfirm(false)} className="px-4 py-2 border border-[#E2DACF] text-xs tracking-[0.14em] uppercase">Annulla</button>
            </div>
          ) : (
            <button data-testid="analytics-reset-btn" onClick={() => setConfirm(true)} className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2DACF] text-xs tracking-[0.14em] uppercase text-[#595652] hover:border-[#C5A059] hover:text-[#1A1A18] transition-colors">
              <RotateCcw size={14} strokeWidth={1.5} /> Azzera contatori
            </button>
          )}
        </div>
      </PageTitle>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {data.funnel.map((f, i) => {
          const prev = i ? data.funnel[i - 1].count : null;
          return (
            <div key={f.key} className="bg-white border border-[#E2DACF] p-5" data-testid={`funnel-${f.key}`}>
              <p className="text-xs text-[#8C8275]">{LABELS[f.key]}</p>
              <p className="font-serif text-4xl mt-2 tabular-nums">{f.count}</p>
              <Delta count={f.count} previous={f.previous} period={period} />
              <div className="h-1 bg-[#F2ECE4] mt-4"><div className="h-1 bg-[#C5A059]" style={{ width: `${(f.count / top) * 100}%` }} /></div>
              {prev !== null && <p className="text-[11px] text-[#8C8275] mt-2">{prev ? Math.round((f.count / prev) * 100) : 0}% dallo step precedente</p>}
            </div>
          );
        })}
      </div>
      <DailyChart />
      <HourlyChart period={period} />
      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border border-[#E2DACF] p-6" data-testid="analytics-avg-cart">
          <p className="text-xs text-[#8C8275]">Valore medio routine al carrello</p>
          <p className="font-serif text-3xl mt-2">{euro(data.avg_cart)}</p>
          <div className="mt-6 space-y-2">
            {OTHER.map((k) => (
              <div key={k} className="flex justify-between text-sm"><span className="text-[#595652]">{k}</span><span className="tabular-nums">{data.events[k] || 0}</span></div>
            ))}
          </div>
          <p className="text-[11px] text-[#8C8275] mt-4">Conteggi per sessioni uniche · periodo: {PERIODS.find(([k]) => k === period)?.[1]}.</p>
        </div>
        <Table title="Sorgenti (UTM source)" testid="analytics-sources" head={["Sorgente", "Visite", "Carrello"]}
          rows={data.sources.map((s) => [s.source, s.visits, s.carts])} />
        <Table title="Prodotti" testid="analytics-products" head={["Prodotto", "Al carrello", "Alternativa", "Rimosso"]}
          rows={data.products.map((p) => [p.name, p.product_selected, p.alternative_selected, p.product_removed])} />
      </div>
    </div>
  );
}

const Table = ({ title, head, rows, testid }) => (
  <div className="bg-white border border-[#E2DACF] p-6 overflow-x-auto" data-testid={testid}>
    <p className="text-xs text-[#8C8275] mb-4">{title}</p>
    {rows.length ? (
      <table className="w-full text-sm">
        <thead><tr>{head.map((h) => <th key={h} className="text-left font-medium text-[#8C8275] text-xs pb-2">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={String(r[0])} className="border-t border-[#F2ECE4]">{r.map((c, j) => <td key={`${r[0]}-${head[j]}`} className="py-2 pr-2 tabular-nums">{c}</td>)}</tr>)}</tbody>
      </table>
    ) : <p className="text-sm text-[#C2B8A8]">Nessun dato ancora.</p>}
  </div>
);
